import React, { useEffect } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

const WIDGET_TYPES = [
  {
    widget_type: 'chat',
    icon: '💬',
    label: 'Chat Widget',
    desc: 'Add a floating chat bubble to your website with links to WhatsApp, Messenger, Instagram, Telegram, and more.',
    features: ['Multiple messaging channels', 'Floating bubble placement', 'Custom branding & colors', 'Targeting rules'],
    color: '#ED5A29',
    bg: '#FFF5F2',
    border: '#ED5A29',
  },
  {
    widget_type: 'ai_summary',
    icon: '🤖',
    label: 'AI Summary',
    desc: 'Embed inline AI provider buttons that let visitors summarize your content using ChatGPT, Claude, Gemini, and more.',
    features: ['ChatGPT, Claude, Gemini & more', 'Inline button placement', 'Custom button styling', 'Targeting rules'],
    color: '#2F855A',
    bg: '#F0FFF4',
    border: '#38A169',
  },
];

export default function WidgetTypeSelector({ site }) {
  const history = useHistory();
  const { siteId } = useParams();

  useEffect(() => {
    try {
      window.analytics?.track('Widget Type Selector Viewed', { site_id: siteId });
    } catch (_) {}
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelect = (widget_type) => {
    try {
      window.analytics?.track('Widget Type Selected', { widget_type, site_id: siteId });
    } catch (_) {}
    history.push(`/sites/${siteId}/widgets/new?widget_type=${widget_type}`);
  };

  return (
    <div style={{
      backgroundColor: '#F2F1F1',
      minHeight: '100vh',
      padding: '32px 0px 0px',
    }}>
      {/* Header */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        marginBottom: '40px',
      }}>
        <button
          onClick={() => history.push(`/sites/${siteId}/widgets`)}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            color: '#777',
            fontSize: '14px',
            padding: '0',
            marginBottom: '24px',
          }}
        >
          <ArrowBackIcon style={{ fontSize: '18px' }} />
          Back to Widgets
        </button>

        <h1 style={{
          color: '#23262A',
          fontWeight: 'bold',
          fontSize: '28px',
          margin: '0 0 8px 0',
        }}>
          Choose a Widget Type
        </h1>
        <p style={{
          color: '#777',
          fontSize: '16px',
          margin: 0,
        }}>
          Select the type of widget you'd like to add to your website.
        </p>
      </div>

      {/* Cards */}
      <div style={{
        maxWidth: '900px',
        margin: '0 auto',
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '24px',
      }}>
        {WIDGET_TYPES.map((type) => (
          <div
            key={type.widget_type}
            onClick={() => handleSelect(type.widget_type)}
            style={{
              background: '#fff',
              borderRadius: '16px',
              border: `2px solid #E5E7EB`,
              padding: '36px 32px',
              cursor: 'pointer',
              transition: 'all 0.2s ease',
              boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
              display: 'flex',
              flexDirection: 'column',
              gap: '20px',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.border = `2px solid ${type.border}`;
              e.currentTarget.style.boxShadow = `0 8px 24px rgba(0,0,0,0.10)`;
              e.currentTarget.style.transform = 'translateY(-2px)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.border = `2px solid #E5E7EB`;
              e.currentTarget.style.boxShadow = `0 2px 8px rgba(0,0,0,0.04)`;
              e.currentTarget.style.transform = 'translateY(0)';
            }}
          >
            {/* Icon + Title */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '16px',
                background: type.bg,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '32px',
                flexShrink: 0,
              }}>
                {type.icon}
              </div>
              <div>
                <div style={{
                  fontWeight: 'bold',
                  fontSize: '20px',
                  color: '#23262A',
                  marginBottom: '4px',
                }}>
                  {type.label}
                </div>
                <div style={{ fontSize: '14px', color: '#777', lineHeight: '1.4' }}>
                  {type.desc}
                </div>
              </div>
            </div>

            {/* Feature list */}
            <ul style={{
              margin: 0,
              padding: 0,
              listStyle: 'none',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}>
              {type.features.map((f) => (
                <li key={f} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontSize: '14px',
                  color: '#555',
                }}>
                  <span style={{
                    width: '18px',
                    height: '18px',
                    borderRadius: '50%',
                    background: type.bg,
                    color: type.color,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '11px',
                    fontWeight: 'bold',
                    flexShrink: 0,
                  }}>
                    ✓
                  </span>
                  {f}
                </li>
              ))}
            </ul>

            {/* CTA */}
            <div style={{
              marginTop: 'auto',
              padding: '12px 0 0 0',
            }}>
              <div style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '6px',
                background: type.color,
                color: '#fff',
                borderRadius: '8px',
                padding: '10px 20px',
                fontSize: '14px',
                fontWeight: 'bold',
              }}>
                Create {type.label} →
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
